import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getGarminDetail } from "../lib/api.js";
import type { GarminProductDetail } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { PackageIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * Garmin detail produktu.
 * Obsah závisí na dostupnosti scrape dat:
 *   - XML only (většina produktů) → krátký popis, 1 obrázek, cena
 *   - + scraped → bohatý HTML popis, galerie, technické specs
 */
export function GarminDetailPage(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<GarminProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getGarminDetail(code)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setActiveImage(d.images[0]?.url ?? null);
      })
      .catch((err) => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) return <LoadingBlock label="Načítám Garmin produkt…" />;
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error}
        </div>
      </div>
    );
  if (!data)
    return <EmptyState title="Produkt nenalezen" description="Zkontrolujte kód v URL." />;

  // Specs seskupené podle `group`
  const specsByGroup = new Map<string, typeof data.specs>();
  for (const s of data.specs) {
    const g = s.group ?? "Technické parametry";
    const arr = specsByGroup.get(g) ?? [];
    arr.push(s);
    specsByGroup.set(g, arr);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <Breadcrumbs
          items={[
            { label: "Garmin", to: "/garmin" },
            ...(data.categoryXml
              ? [
                  {
                    label: data.categoryXml,
                    to: `/garmin?category=${encodeURIComponent(data.categoryXml)}`,
                  },
                ]
              : []),
            { label: data.name },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,480px)_1fr] gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-white rounded-2xl border border-zinc-200 shadow-card flex items-center justify-center overflow-hidden">
            {activeImage ? (
              <img
                src={activeImage}
                alt={data.name}
                className="w-full h-full object-contain p-6"
              />
            ) : (
              <div className="text-zinc-300">
                <PackageIcon size={48} />
              </div>
            )}
          </div>
          {data.images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2">
              {data.images.slice(0, 10).map((img) => (
                <button
                  key={img.url}
                  type="button"
                  onClick={() => setActiveImage(img.url)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                    activeImage === img.url ? "border-ktm-orange" : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-contain p-1 bg-zinc-50" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="orange">Garmin</Badge>
            {data.categoryXml && (
              <span className="text-sm text-zinc-500">{data.categoryXml}</span>
            )}
            {data.scrapeStatus === "ok" && (
              <Badge tone="blue">Rozšířený obsah</Badge>
            )}
            {data.scrapeStatus === "not_found" && (
              <Badge tone="neutral">Jen z feedu</Badge>
            )}
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
            {data.name}
          </h1>

          {data.xmlName && data.xmlName !== data.name && (
            <p className="mt-1 text-sm text-zinc-500">{data.xmlName}</p>
          )}

          {data.descriptionShort && (
            <p className="mt-3 text-sm text-zinc-700">{data.descriptionShort}</p>
          )}

          {/* Price */}
          <div className="mt-5 flex items-end justify-between flex-wrap gap-3">
            <div>
              {data.priceMocVat != null ? (
                <>
                  <div className="text-3xl font-semibold text-zinc-900">
                    {data.priceMocVat.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })}{" "}
                    Kč
                  </div>
                  {data.priceVocNoVat != null && (
                    <div className="text-sm text-zinc-500">
                      nákup {data.priceVocNoVat.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč bez DPH
                    </div>
                  )}
                </>
              ) : (
                <span className="text-zinc-400">Bez ceny</span>
              )}
            </div>
            <Badge tone={data.inStock ? "green" : "neutral"}>
              {data.avail ?? (data.inStock ? "Skladem" : "Není skladem")}
            </Badge>
          </div>

          {/* Meta */}
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <MetaItem label="Kód (SKU)">
              <span className="font-mono">{data.code}</span>
            </MetaItem>
            {data.ean && (
              <MetaItem label="EAN">
                <span className="font-mono">{data.ean}</span>
              </MetaItem>
            )}
            {data.manufacturer && (
              <MetaItem label="Výrobce">{data.manufacturer}</MetaItem>
            )}
            {data.unit && <MetaItem label="Jednotka">{data.unit}</MetaItem>}
          </dl>

          {data.productUrl && (
            <a
              href={data.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-ktm-orange hover:bg-ktm-orange-600 text-white text-sm font-medium transition-colors"
            >
              Otevřít na garmin.com
              <ChevronRightIcon size={14} />
            </a>
          )}
        </div>
      </div>

      {data.descriptionHtml && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Popis produktu
          </h2>
          <div
            className="prose prose-sm max-w-none bg-white rounded-2xl border border-zinc-200 p-6 shadow-card"
            dangerouslySetInnerHTML={{ __html: data.descriptionHtml }}
          />
        </section>
      )}

      {data.specs.length > 0 && (
        <section className="mt-10">
          <h2 className="text-base font-semibold uppercase tracking-tight text-zinc-900 mb-3">
            Technické parametry
          </h2>
          {Array.from(specsByGroup.entries()).map(([group, items]) => (
            <div key={group} className="mb-6">
              {group !== "Technické parametry" && (
                <h3 className="text-sm font-semibold text-zinc-700 mb-2">{group}</h3>
              )}
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((s, i) => (
                  <div
                    key={`${s.key}-${i}`}
                    className="flex items-start justify-between gap-3 bg-white rounded-lg border border-zinc-200 px-4 py-2"
                  >
                    <dt className="text-sm text-zinc-500 shrink-0">{s.key}</dt>
                    <dd className="text-sm font-medium text-zinc-900 text-right">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </section>
      )}

      {data.scrapeStatus !== "ok" && !data.descriptionHtml && data.specs.length === 0 && (
        <section className="mt-10 rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500">
          Rozšířený obsah z garmin.com zatím nebyl stažen.
          {data.scrapeStatus === "not_found" &&
            " Pro tento kód nebyla nalezena produktová stránka na garmin.com (pravděpodobně distribuční položka od třetí strany — např. RAM Mounts)."}
        </section>
      )}
    </div>
  );
}

function MetaItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-zinc-700">{children}</dd>
    </div>
  );
}
